import type { GetServerSideProps, GetServerSidePropsContext, NextPage } from "next";
import React from "react";
import { getNewsPage } from "../../api/graphql/queries";
import NewsListing from "../../cmsPages/NewsListing";
import { initializeApollo } from "../../lib/apolloClient";
import { getFrontendHostname, getTheme } from '../../lib/config';
import { NewsListingFragment } from './../../__generated__/graphql';
import Error404 from "./../../errorPages/Error404";
import Error500 from "./../../errorPages/Error500";

type BlogsPageProps = {
  data: {
    news: NewsListingFragment
  };
  pageUrl: string;
  siteName: string;
  homepageUrl: string;
  errorCode?: number;
};

const BlogsPage: NextPage<BlogsPageProps> = (props: BlogsPageProps) => {

  if (props.errorCode === 500) {
    return <Error500 />;
  }
  else if (props.errorCode) {
    return <Error404 />;
  }
  return <NewsListing type="blog" pageUrl={props.pageUrl} {...props.data.news} />;
};

export default BlogsPage;

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const { res, req } = context;
  const theme = getTheme();
  const path = context.resolvedUrl;
  const hostname = getFrontendHostname();

  let pageUrl;
  if (req) {
    // Server side rendering
    pageUrl = 'https://' + hostname + req.url;
  } else {
    // Client side rendering
    pageUrl = window.location.href;
  }
  const url = new URL(pageUrl);
  // ZSL and Zoos use different URLs to reach this page. Only allow the correct URL so we don't create duplicate content.
  if (url.pathname.includes('plan-your-visit')
    || (theme === 'zsl' && (url.pathname.includes('zoo-stories') || url.pathname.includes('/blog')))
    || (theme !== 'zsl' && (url.pathname.includes('news-and-events') || url.pathname.includes('/feature')))) {
    res.statusCode = 404;
    return {
      props: {
        theme: theme,
        pageUrl: pageUrl,
        hostname: hostname,
        errorCode: 404
      }
    };
  }

  // Get current params for the query.
  const params = url.searchParams;
  // Page number
  const page = (Number.parseInt(params.get('page') || '0|') - 1) || 0;
  // Tag filters.
  const dept = params.getAll('blog_department');
  const flatDept = dept ? dept.map((value) => value.split(',')).flat() : null;

  const apolloClient = initializeApollo();
  const result = await apolloClient.query({
    query: getNewsPage,
    variables: {
      type: "blog",
      page: Math.max(page, 0),
      filterDept: flatDept || null,
    }
  })
  .catch((e) => {
    res.statusCode = 500;
    return {
      data: {
        route:{}
      },
      errorCode: 500
    };
  });

  return {
    props: {
      theme: theme,
      pageUrl: pageUrl,
      hostname: hostname,
      ...result
    },
  };
};
